import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import { registerSchema, type RegisterFormValues } from '@/modules/auth/schemas/auth.schemas';
import { setPendingVerificationEmail } from '@/modules/auth/lib/pendingVerificationEmail';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { isApiError } from '@/shared/lib/apiClient';
import { PasswordStrength } from '@/modules/auth/components/PasswordStrength';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { PasswordInput } from '@/shared/components/ui/password-input';
import { Label } from '@/shared/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';

export const Route = createFileRoute('/auth/register')({
  component: RegisterPage,
});

function RegisterPage() {
  const { t, i18n } = useTranslation();
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      locale: (i18n.language === 'ar' ? 'ar' : 'en') as 'ar' | 'en',
    },
  });

  const password = watch('password', '');

  const onSubmit = async (values: RegisterFormValues) => {
    try {
      await registerUser({
        email: values.email,
        password: values.password,
        fullName: values.fullName,
        locale: values.locale,
      });
      setPendingVerificationEmail(values.email);
      await navigate({ to: '/auth/verify-email-sent' });
    } catch (err) {
      let message = t('auth.errors.generic');
      if (isApiError(err) && err.status === 429) {
        message = t('auth.errors.tooManyRequests', {
          seconds: err.retryAfterSeconds ?? 60,
        });
      }
      setError('root', { message });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('auth.register.title')}</CardTitle>
        <CardDescription>{t('auth.register.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="fullName">{t('auth.register.fullName')}</Label>
            <Input
              id="fullName"
              type="text"
              autoComplete="name"
              placeholder={t('auth.register.fullNamePlaceholder')}
              aria-invalid={!!errors.fullName}
              {...register('fullName')}
            />
            {errors.fullName && (
              <p className="text-xs text-destructive">{t(errors.fullName.message ?? '')}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">{t('auth.register.email')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('auth.register.emailPlaceholder')}
              aria-invalid={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{t(errors.email.message ?? '')}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">{t('auth.register.password')}</Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              placeholder={t('auth.register.passwordPlaceholder')}
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            <PasswordStrength password={password} />
            {errors.password && (
              <p className="text-xs text-destructive">{t(errors.password.message ?? '')}</p>
            )}
          </div>

          <Button type="submit" className="w-full" isLoading={isSubmitting}>
            {isSubmitting ? t('auth.register.submitting') : t('auth.register.submit')}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            {t('auth.register.haveAccount')}{' '}
            <Link to="/auth/login" className="text-primary hover:underline font-medium">
              {t('auth.register.signIn')}
            </Link>
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
